import {
  CreditCardListQueryParams,
  CreditCardMonthStatistics,
  CreditCardRequest,
  CreditCardResponse2,
  PageCreditCardWithSchedules,
  UnpaidCreditCardDto,
} from '../../model/Credit-card.model';
import axiosInstance from '../../utils/axiosInstance';

class CreditCardService {
  private readonly baseURL = '/credit-card';

  /** Spring expects repeated keys: bankIds=1&bankIds=2 */
  private appendFilterQueryParams(
    sp: URLSearchParams,
    filter: Record<string, unknown>
  ): void {
    Object.entries(filter).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        if (v.length === 0) return;
        v.forEach((item) => sp.append(k, String(item)));
        return;
      }
      if (typeof v === 'boolean') {
        sp.append(k, String(v));
        return;
      }
      if (v === '') return;
      sp.append(k, String(v));
    });
  }

  async getCreditCardList(): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(this.baseURL);
      return data;
    } catch (error) {
      console.error('Lỗi khi tải danh sách thẻ tín dụng:', error);
      throw new Error(
        'Lỗi khi tải danh sách thẻ tín dụng: ' + this.getErrorMessage(error)
      );
    }
  }

  async getCardsByCustomerId(
    customerId: number
  ): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/customer/${customerId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng của khách hàng ${customerId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng của khách hàng: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getUnpaidCards(): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(`${this.baseURL}/unpaid`);
      return data;
    } catch (error) {
      console.error('Lỗi khi tải thẻ tín dụng chưa thanh toán:', error);
      throw new Error(
        'Lỗi khi tải thẻ tín dụng chưa thanh toán: ' +
          this.getErrorMessage(error)
      );
    }
  }

  async getUnpaidCreditCardsByMonthAndYear(
    month: number,
    year: number
  ): Promise<UnpaidCreditCardDto[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/unpaid/${month}/${year}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng chưa thanh toán cho tháng ${month}/${year}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng chưa thanh toán cho kỳ: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getCompletedCreditCardsByMonthYearAndCustomerId(
    month: number,
    year: number,
    customerId: number
  ): Promise<UnpaidCreditCardDto[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/completed/${customerId}`,
        {
          params: { month, year },
        }
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng đã thanh toán của khách hàng ${customerId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng đã thanh toán: ` + this.getErrorMessage(error)
      );
    }
  }

  async getUnpaidCreditCardsByMonthYearAndCustomerId(
    month: number,
    year: number,
    customerId: number
  ): Promise<UnpaidCreditCardDto[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/unpaid/${customerId}`,
        {
          params: { month, year },
        }
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng chưa thanh toán của khách hàng ${customerId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng chưa thanh toán: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getUnpaidCreditCardsByUserMonthAndYear(
    userId: number,
    month: number,
    year: number
  ): Promise<UnpaidCreditCardDto[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/unpaid-assigned/${userId}/${month}/${year}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng chưa thanh toán của người dùng ${userId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng chưa thanh toán của người dùng: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardById(id: number): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.get(`${this.baseURL}/${id}`);
      return data;
    } catch (error) {
      console.error(`Lỗi khi tải thông tin thẻ tín dụng ${id}:`, error);
      throw new Error(
        `Lỗi khi tải thông tin thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async getAllCreditCards(): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(this.baseURL);
      return data;
    } catch (error) {
      console.error('Lỗi khi tải tất cả thẻ tín dụng:', error);
      throw new Error(
        'Lỗi khi tải tất cả thẻ tín dụng: ' + this.getErrorMessage(error)
      );
    }
  }

  async getAllCreditCardsWithSchedules(): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/with-schedules`
      );
      return data;
    } catch (error) {
      console.error(
        'Lỗi khi tải danh sách thẻ tín dụng với lịch đáo hạn:',
        error
      );
      throw new Error(
        'Lỗi khi tải danh sách thẻ tín dụng với lịch đáo hạn: ' +
          this.getErrorMessage(error)
      );
    }
  }

  /**
   * Full list (no pagination). Use for Excel export or bulk flows when all rows are required.
   * The credit-cards page uses getCreditCardsWithSchedulesByMonthPaged instead.
   */
  async getAllCreditCardsWithSchedulesByMonth(
    month: number,
    year: number
  ): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/with-schedules-by-month`,
        {
          params: { month, year },
        }
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải danh sách thẻ tín dụng với lịch đáo hạn tháng ${month}/${year}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải danh sách thẻ tín dụng với lịch đáo hạn tháng ${month}/${year}: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardsWithSchedulesByMonthPaged(
    month: number,
    year: number,
    query: CreditCardListQueryParams
  ): Promise<PageCreditCardWithSchedules> {
    try {
      const { page = 0, size = 20, ...filterRest } = query;
      const sp = new URLSearchParams();
      sp.set('month', String(month));
      sp.set('year', String(year));
      sp.set('page', String(page));
      sp.set('size', String(size));
      this.appendFilterQueryParams(sp, filterRest as Record<string, unknown>);
      const { data } = await axiosInstance.get(
        `${this.baseURL}/with-schedules-by-month/paged?${sp.toString()}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải danh sách thẻ (phân trang) tháng ${month}/${year}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải danh sách thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardMonthStatistics(
    month: number,
    year: number,
    filter: Omit<CreditCardListQueryParams, 'page' | 'size'>
  ): Promise<CreditCardMonthStatistics> {
    try {
      const sp = new URLSearchParams();
      sp.set('month', String(month));
      sp.set('year', String(year));
      this.appendFilterQueryParams(sp, filter as Record<string, unknown>);
      const { data } = await axiosInstance.get(
        `${this.baseURL}/with-schedules-by-month/stats?${sp.toString()}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thống kê thẻ tháng ${month}/${year}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thống kê thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async createCreditCard(
    customerId: number,
    creditCardData: CreditCardRequest
  ): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.post(
        `${this.baseURL}/create/${customerId}`,
        creditCardData
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tạo thẻ tín dụng cho khách hàng ${customerId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tạo thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async deleteCreditCard(id: number): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseURL}/delete/${id}`);
    } catch (error) {
      console.error(`Lỗi khi xóa thẻ tín dụng ${id}:`, error);
      throw new Error(
        `Lỗi khi xóa thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async softDeleteCreditCard(id: number): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.put(
        `${this.baseURL}/soft-delete/${id}`
      );
      return data;
    } catch (error) {
      console.error(`Lỗi khi vô hiệu hóa thẻ tín dụng ${id}:`, error);
      throw new Error(
        `Lỗi khi vô hiệu hóa thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async hardDeleteCreditCard(id: number): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseURL}/hard-delete/${id}`);
    } catch (error) {
      console.error(`Lỗi khi xóa cứng thẻ tín dụng ${id}:`, error);
      throw new Error(
        `Lỗi khi xóa cứng thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async updateCreditCard(
    id: number,
    creditCardRequestDto: CreditCardRequest
  ): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.put(
        `${this.baseURL}/edit/${id}`,
        creditCardRequestDto
      );
      return data;
    } catch (error) {
      console.error(`Lỗi khi cập nhật thẻ tín dụng ${id}:`, error);
      throw new Error(
        `Lỗi khi cập nhật thẻ tín dụng: ` + this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardsAssignedToEmployeeWithRoleEmployee(
    userId: number
  ): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/assigned/${userId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng được gán cho nhân viên ${userId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng được gán: ` + this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardByMaturityScheduleId(
    scheduleId: number
  ): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/by-schedule/${scheduleId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng theo lịch đáo hạn ${scheduleId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng theo lịch đáo hạn: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardsOfCustomersAssignedToEmployee(
    userId: number
  ): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/assigned-customer-cards/${userId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng của khách hàng được gán cho nhân viên ${userId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng của khách hàng được gán: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getActiveCreditCardsOfCustomersAssignedToEmployee(
    userId: number
  ): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/active-assigned-customer-cards/${userId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng đang hoạt động của khách hàng được gán cho nhân viên ${userId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng đang hoạt động: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async getAllActiveCreditCards(): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/active-credit-cards`
      );
      return data;
    } catch (error) {
      console.error('Lỗi khi tải tất cả thẻ tín dụng đang hoạt động:', error);
      throw new Error(
        'Lỗi khi tải tất cả thẻ tín dụng đang hoạt động: ' +
          this.getErrorMessage(error)
      );
    }
  }

  async updateCreditCardNewStatus(
    id: number,
    newStatus: string
  ): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.put(
        `${this.baseURL}/${id}/new-status`,
        newStatus,
        {
          headers: {
            'Content-Type': 'text/plain',
          },
        }
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi cập nhật trạng thái mới của thẻ tín dụng ${id}:`,
        error
      );
      throw new Error(
        `Lỗi khi cập nhật trạng thái mới: ` + this.getErrorMessage(error)
      );
    }
  }

  async toggleCreditCardNewStatus(id: number): Promise<CreditCardResponse2> {
    try {
      const { data } = await axiosInstance.put(
        `${this.baseURL}/${id}/toggle-new-status`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi toggle trạng thái mới của thẻ tín dụng ${id}:`,
        error
      );
      throw new Error(
        `Lỗi khi toggle trạng thái mới: ` + this.getErrorMessage(error)
      );
    }
  }

  async getCreditCardsWithoutMaturityScheduleInMonth(
    month: number,
    year: number
  ): Promise<CreditCardResponse2[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/without-maturity-schedule/${month}/${year}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải thẻ tín dụng không có kỳ đáo hạn trong tháng ${month}/${year}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải thẻ tín dụng không có kỳ đáo hạn: ` +
          this.getErrorMessage(error)
      );
    }
  }

  async exportCreditCardsByMonth(month: number, year: number): Promise<Blob> {
    try {
      const response = await axiosInstance.get(
        `${this.baseURL}/export-by-month`,
        {
          params: { month, year },
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Lỗi khi export thẻ tín dụng tháng ${month}/${year}:`,
        error
      );
      throw new Error(
        `Lỗi khi export thẻ tín dụng tháng ${month}/${year}: ` +
          this.getErrorMessage(error)
      );
    }
  }

  // Helper method to extract error messages
  private getErrorMessage(error: any): string {
    return (
      error.response?.data?.message || error.message || 'Lỗi không xác định'
    );
  }
}

const creditCardServiceInstance = new CreditCardService();
export default creditCardServiceInstance;
